import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { createPage, deleteTemplate, listSavedTemplates, type CrmFormDraft, type NewPageInput, type SavedTemplate } from '../api';
import { localizeAssets, newSectionFilled, syncLegacySections, type PageContent, type PageTheme } from '../model/pages';
import type { PreviewForm } from '../render/FormMock';
import {
  PAGE_STARTERS, STARTER_CATEGORIES, previewFormFromDraft, starterForm, starterPreviewForm, type StarterCategory,
} from '../starters/page-starters';
import { useConfirm, useToast } from '../ui/feedback';
import { PlusIcon } from '../icons';
import TemplatePreview from '../preview/TemplatePreview';
import TemplateThumb from '../preview/TemplateThumb';
import TemplatePreviewModal, { type PreviewTemplate } from '../preview/TemplatePreviewModal';

/** One card of the gallery: a built-in starter or a template someone saved
 *  from the editor (More, Save as template). */
export interface GalleryTemplate extends PreviewTemplate {
  source: 'starter' | 'saved';
  /** The starter's key or the saved template's id. */
  id: string;
  name: string;
  description?: string;
  /** The gallery tab it shows under (absent: only under All). */
  category?: StarterCategory;
  content: PageContent;
  theme: PageTheme;
  /** The CRM form the new page gets: the server creates it as a draft linked
   *  to the page, and it goes live when the page is published. */
  form: CrmFormDraft | null;
  /** A still of that form for the previews (FormMock). */
  previewForm: PreviewForm | null;
}

/** The 11 built-in starters, most used first. */
export function starterTemplates(): GalleryTemplate[] {
  return PAGE_STARTERS.map((s) => ({
    source: 'starter', id: s.key, name: s.name, description: s.description, category: s.category,
    content: s.content, theme: s.theme, form: starterForm(s), previewForm: starterPreviewForm(s),
  }));
}

/** A saved template as a gallery card. */
export function savedToTemplate(t: SavedTemplate): GalleryTemplate {
  return {
    source: 'saved', id: t.id, name: t.name, description: t.description || undefined,
    content: t.content ?? {}, theme: t.theme ?? {}, form: t.form ?? null, previewForm: previewFormFromDraft(t.form),
  };
}

/** A Blank page's look: the clean style with Inter. */
export const BLANK_PAGE_THEME: PageTheme = { style: 'clean', font: 'inter' };

/** A Blank page's starting content: one hero with placeholder words to click
 *  and type over, as step 1. */
export function blankPageContent(): PageContent {
  let n = 0;
  const mkId = () => `s_${Date.now().toString(36)}_${n++}`;
  return syncLegacySections({ sections: [newSectionFilled('hero', mkId())], thank_you: {} });
}

/** What POST /api/pages gets for a new page: a template's content and theme
 *  and the CRM form it asks for. Template images stay RELATIVE
 *  (/template-assets/...): every host that serves the page (the app, each
 *  custom domain) serves them itself, so they never load cross-site; the server
 *  makes og:image absolute for share cards. Null = Blank page. */
export function newPageInput(name: string, tpl: GalleryTemplate | null): NewPageInput {
  if (!tpl) return { name, content: blankPageContent(), theme: { ...BLANK_PAGE_THEME } };
  return {
    name,
    content: syncLegacySections(tpl.content),
    theme: { ...tpl.theme },
    form: tpl.form,
  };
}

const keyOf = (t: GalleryTemplate) => `${t.source}:${t.id}`;
type Tab = 'all' | 'saved' | StarterCategory;

/** Full-screen "New page" gallery: a name, the template tabs, a Blank page
 *  card and every template as a live preview, with a full Preview of each
 *  and Use this template there. Create (or Use this template) makes the page
 *  and opens the editor. Esc closes it; the page under it does not scroll
 *  while it is open. */
export default function NewPageModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const nav = useNavigate();
  const toast = useToast();
  const confirm = useConfirm();

  const [name, setName] = useState('');
  // The name follows the selected template until the user types their own.
  const [nameTouched, setNameTouched] = useState(false);
  const [tpls, setTpls] = useState<GalleryTemplate[]>(starterTemplates);
  const [selected, setSelected] = useState(''); // '' = Blank page; else keyOf(template)
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState<Tab>('all');
  const [previewing, setPreviewing] = useState<GalleryTemplate | null>(null);
  const active = useRef<symbol | null>(null);
  const creating = useRef<symbol | null>(null);
  const choice = useRef({ selected, nameTouched });
  choice.current = { selected, nameTouched };
  const closeGallery = useCallback(() => {
    active.current = null;
    creating.current = null;
    onClose();
  }, [onClose]);
  // Card previews: absolute image addresses, built once per template list
  // (not on every keystroke in the name field).
  const cards = useMemo(() => tpls.map((t) => ({ t, content: localizeAssets(t.content, window.location.origin) })), [tpls]);
  const hasSaved = tpls.some((t) => t.source === 'saved');

  // Start fresh and (re)load the saved templates every time the gallery opens.
  useEffect(() => {
    if (!open) return;
    active.current = Symbol('gallery');
    creating.current = null;
    setName(''); setNameTouched(false); setSelected(''); setBusy(false); setTab('all'); setPreviewing(null);
    setTpls(starterTemplates());
    let live = true;
    listSavedTemplates()
      .then((saved) => { if (live) setTpls([...starterTemplates(), ...saved.map(savedToTemplate)]); })
      .catch(() => { /* the starters still show */ });
    return () => { live = false; active.current = null; creating.current = null; };
  }, [open]);

  // Esc closes (unless a preview is open on top) and the page behind stays put.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !previewing && !e.defaultPrevented) closeGallery(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [open, closeGallery, previewing]);

  // The Saved tab goes away with the last saved template.
  useEffect(() => { if (tab === 'saved' && !hasSaved) setTab('all'); }, [tab, hasSaved]);

  async function make(pageName: string, tpl: GalleryTemplate | null) {
    if (!active.current || creating.current) return;
    const request = Symbol('create-page');
    creating.current = request;
    setBusy(true);
    try {
      const page = await createPage(newPageInput(pageName, tpl));
      if (active.current && creating.current === request) nav(`/pages/${page.id}`);
    } catch (e) {
      if (active.current && creating.current === request) toast((e as Error)?.message || 'Couldn’t create the page. Try again.', 'error');
    } finally {
      if (active.current && creating.current === request) { creating.current = null; setBusy(false); }
    }
  }

  function create() {
    const trimmed = name.trim();
    if (!trimmed || busy) return;
    void make(trimmed, selected ? tpls.find((t) => keyOf(t) === selected) ?? null : null);
  }

  // Pick a template card (or Blank, tpl = null). An untouched name follows it.
  function pick(tpl: GalleryTemplate | null) {
    setSelected(tpl ? keyOf(tpl) : '');
    if (!nameTouched) setName(tpl ? tpl.name : '');
  }

  // "Use this template" in the full preview: create the page right away and
  // open the editor. Named after the template unless the user typed a name.
  function createFromTemplate(tpl: GalleryTemplate) {
    if (busy) return;
    const pageName = (nameTouched && name.trim()) || tpl.name;
    setSelected(keyOf(tpl)); setName(pageName);
    void make(pageName, tpl);
  }

  async function removeTemplate(tpl: GalleryTemplate) {
    const session = active.current;
    if (tpl.source !== 'saved' || !session) return;
    const ok = await confirm({ title: `Delete the “${tpl.name}” template?`, body: 'Pages already made from it stay as they are.', confirmLabel: 'Delete template', danger: true });
    if (!ok || active.current !== session) return;
    try {
      await deleteTemplate(tpl.id);
      if (active.current !== session) return;
      setTpls((all) => all.filter((x) => keyOf(x) !== keyOf(tpl)));
      if (choice.current.selected === keyOf(tpl)) {
        setSelected('');
        if (!choice.current.nameTouched) setName('');
      }
      toast('Template deleted');
    } catch (e) {
      if (active.current === session) toast((e as Error)?.message || 'Couldn’t delete the template. Try again.', 'error');
    }
  }

  const closePreview = useCallback(() => setPreviewing(null), []);

  if (!open || typeof document === 'undefined') return null;
  const tabs: { key: Tab; label: string }[] = [
    { key: 'all', label: 'All' },
    ...STARTER_CATEGORIES.filter((c) => tpls.some((t) => t.category === c.key)),
    ...(hasSaved ? [{ key: 'saved' as const, label: 'Saved' }] : []),
  ];
  const shown = cards.filter(({ t }) => tab === 'all' || (tab === 'saved' ? t.source === 'saved' : t.category === tab));

  return createPortal(
    // pe-scope: the gallery lives on document.body, outside the Pages list.
    <div className="pe-scope">
      <div role="dialog" aria-modal="true" aria-label="New page" className="fixed inset-0 z-[1050] overflow-y-auto overscroll-contain bg-bg">
        {/* Sticky header: title, name, Cancel / Create. */}
        <header className="sticky top-0 z-10 flex flex-wrap items-center gap-3 border-b border-border bg-bg px-4 py-3 sm:px-6">
          <h2 className="shrink-0 text-base font-semibold text-text">New page</h2>
          <input className="input min-w-0 flex-1 basis-40 sm:max-w-xs [@media(pointer:coarse)]:h-11" autoFocus value={name}
            aria-label="Page name" placeholder="Page name" maxLength={120}
            onChange={(e) => { setName(e.target.value); setNameTouched(e.target.value.trim() !== ''); }}
            onKeyDown={(e) => { if (e.key === 'Enter') create(); }} />
          <div className="ml-auto flex shrink-0 items-center gap-2">
            <button type="button" className="btn-ghost [@media(pointer:coarse)]:min-h-11" onClick={closeGallery}>Cancel</button>
            <button type="button" className="btn-primary [@media(pointer:coarse)]:min-h-11" disabled={busy || !name.trim()} onClick={create}>
              {busy ? 'Creating…' : 'Create'}
            </button>
          </div>
        </header>

        <div className="mx-auto max-w-6xl px-4 py-5 sm:px-6">
          <div className="ptabs mb-4" role="tablist" aria-label="Template type">
            {tabs.map((c) => (
              <button key={c.key} type="button" role="tab" aria-selected={tab === c.key} onClick={() => setTab(c.key)}
                className={`ptab [@media(pointer:coarse)]:min-h-11 ${tab === c.key ? 'ptab-active' : ''}`}>{c.label}</button>
            ))}
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {tab === 'all' && (
              <button type="button" onClick={() => pick(null)} aria-pressed={selected === ''}
                className={`overflow-hidden rounded-2xl border bg-surface p-0 text-left transition-colors hover:border-borderHi ${selected === '' ? 'border-primary ring-1 ring-primary' : 'border-border'}`}>
                <span className="grid h-[300px] place-items-center bg-surface2">
                  <span className="grid h-10 w-10 place-items-center rounded-full border border-borderHi text-textDim"><PlusIcon size={18} /></span>
                </span>
                <span className="block border-t border-border px-3 py-2.5">
                  <span className="block text-sm font-medium text-text">Blank page</span>
                  <span className="block text-xs text-textDim">Start from scratch.</span>
                </span>
              </button>
            )}
            {shown.map(({ t, content }) => {
              const isSel = selected === keyOf(t);
              return (
                <div key={keyOf(t)} data-template={keyOf(t)}
                  className={`group relative overflow-hidden rounded-2xl border bg-surface transition-colors hover:border-borderHi ${isSel ? 'border-primary ring-1 ring-primary' : 'border-border'}`}>
                  <button type="button" onClick={() => pick(t)} aria-pressed={isSel} aria-label={`Use ${t.name}`} className="block w-full p-0 text-left">
                    <TemplatePreview content={content} theme={t.theme} height={300} flush previewForm={t.previewForm} title={`${t.name} template`}
                      fallback={<TemplateThumb content={t.content} theme={t.theme} />} />
                    <span className="block border-t border-border px-3 py-2.5">
                      <span className="flex min-w-0 items-center gap-2">
                        <span className="truncate text-sm font-medium text-text">{t.name}</span>
                        {t.source === 'saved' && <span className="shrink-0 rounded bg-text/[0.07] px-1.5 text-[11px] font-medium leading-4 text-textDim">Saved</span>}
                      </span>
                      {t.description && <span className="mt-0.5 line-clamp-2 block text-xs text-textDim">{t.description}</span>}
                    </span>
                  </button>
                  <div className="absolute right-2 top-2 flex gap-1.5 opacity-100 transition-opacity [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover:opacity-100 [@media(hover:hover)]:group-focus-within:opacity-100">
                    <button type="button" className="btn-ghost btn-sm [@media(pointer:coarse)]:min-h-11" aria-label={`Preview ${t.name}`} onClick={() => setPreviewing(t)}>Preview</button>
                    {t.source === 'saved' && (
                      <button type="button" className="btn-ghost btn-sm text-danger [@media(pointer:coarse)]:min-h-11" aria-label={`Delete template ${t.name}`}
                        onClick={() => { void removeTemplate(t); }}>Delete</button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <TemplatePreviewModal tpl={previewing} busy={busy} onClose={closePreview} onUse={createFromTemplate} />
      </div>
    </div>,
    document.body,
  );
}
